<template>
  <div class="log-panel">
    <div class="toolbar">
      <el-select v-model="searchField" class="toolbar-select">
        <el-option v-for="item in searchFieldOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-input
        v-model="searchKeyword"
        clearable
        :placeholder="searchPlaceholder"
        class="toolbar-input"
      />
      <el-select v-model="statusFilter" clearable placeholder="状态" class="toolbar-select">
        <el-option v-for="item in statusOptions" :key="item.value" :label="item.label" :value="item.value" />
      </el-select>
      <el-button type="primary" :loading="loading" @click="fetchTickets">刷新</el-button>
      <el-button
        v-if="userStore.isAdmin"
        type="success"
        :loading="exporting"
        @click="exportLogs"
      >
        导出处理日志 Excel
      </el-button>
    </div>

    <div class="log-layout">
      <el-card class="list-card" shadow="never">
        <template #header>
          <div class="card-header">
            <div>
              <div class="card-title">处理日志列表</div>
              <div class="card-subtitle">先在上方选择一条工单，再在下方查看基础信息、处理信息和处理日志</div>
            </div>
            <el-tag type="info" effect="plain">独立日志菜单</el-tag>
          </div>
        </template>

        <el-table
          v-loading="loading"
          :data="paginatedTickets"
          row-key="id"
          border
          stripe
          height="340"
          highlight-current-row
          @current-change="handleCurrentChange"
          @row-click="handleCurrentChange"
        >
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="productName" label="产品" min-width="120" />
          <el-table-column prop="customerName" label="客户/门店" min-width="130" />
          <el-table-column prop="storeAuthCode" label="授权码/地址" min-width="130" />
          <el-table-column label="状态" width="110">
            <template #default="{ row }">
              <el-tag :type="getStatusType(row.status)">{{ getStatusText(row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="时效提醒" min-width="220">
            <template #default="{ row }">
              <div class="sla-cell">
                <el-tag size="small" :type="getSlaType(row)">{{ getSlaLabel(row) }}</el-tag>
                <div class="sla-subtext">{{ getSlaSubtext(row) }}</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="处理人" min-width="110">
            <template #default="{ row }">
              {{ formatPersonDisplay(row.processedBy || row.assignee) }}
            </template>
          </el-table-column>
          <el-table-column label="操作" width="90" fixed="right">
            <template #default="{ row }">
              <el-button type="primary" text @click.stop="selectAndLoad(row)">查看</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div class="pagination-wrapper">
          <el-pagination
            v-model:current-page="currentPage"
            :page-size="pageSize"
            layout="total, prev, pager, next"
            :total="filteredTickets.length"
          />
        </div>
      </el-card>

      <el-card ref="detailCardRef" class="detail-card" shadow="never">
        <template #header>
          <div class="card-header detail-card-header">
            <div class="card-header-main">
              <div class="card-title">处理日志详情</div>
              <div class="card-subtitle">工单基础信息、处理信息和日志统一放在下方查看</div>
            </div>
            <el-button v-if="detailTarget?.id" text type="primary" @click="refreshDetail">刷新详情</el-button>
          </div>
        </template>

        <template v-if="detailTarget">
          <div class="summary-card">
            <div>
              <div class="summary-title">
                <span>{{ detailTarget.title || detailTarget.productName || `工单 #${detailTarget.id}` }}</span>
                <el-tag :type="getStatusType(detailTarget.status)" effect="dark">{{ getStatusText(detailTarget.status) }}</el-tag>
              </div>
              <div class="summary-meta">
                <span>{{ detailTarget.workOrderNo || `工单 #${detailTarget.id}` }}</span>
                <span>{{ detailTarget.customerName || '—' }}</span>
                <span>{{ detailTarget.contact || '—' }}</span>
                <span>{{ detailTarget.phone || '—' }}</span>
              </div>
            </div>
            <div class="summary-assignee">
              <div class="summary-label">当前处理人</div>
              <div class="summary-value">{{ formatPersonDisplay(detailTarget.processedBy || detailTarget.assignee) }}</div>
            </div>
          </div>

          <div class="info-grid">
            <div class="info-card">
              <div class="section-title">基础信息</div>
              <el-descriptions :column="2" border>
                <el-descriptions-item label="工单ID">{{ detailTarget.id }}</el-descriptions-item>
                <el-descriptions-item label="工单编号">{{ detailTarget.workOrderNo || '—' }}</el-descriptions-item>
                <el-descriptions-item label="客户/门店">{{ detailTarget.customerName || '—' }}</el-descriptions-item>
                <el-descriptions-item label="授权码/地址">{{ detailTarget.storeAuthCode || '—' }}</el-descriptions-item>
                <el-descriptions-item label="联系人">{{ detailTarget.contact || '—' }}</el-descriptions-item>
                <el-descriptions-item label="联系电话">{{ detailTarget.phone || '—' }}</el-descriptions-item>
                <el-descriptions-item label="产品名称">{{ detailTarget.productName || '—' }}</el-descriptions-item>
                <el-descriptions-item label="产品型号">{{ detailTarget.productModel || '—' }}</el-descriptions-item>
                <el-descriptions-item label="报单时间">{{ formatDate(detailTarget.reportTime || detailTarget.createdAt) }}</el-descriptions-item>
                <el-descriptions-item label="提单人">{{ formatPersonDisplay(detailTarget.createdBy, '—') }}</el-descriptions-item>
                <el-descriptions-item label="服务内容" :span="2">{{ detailTarget.serviceContent || '—' }}</el-descriptions-item>
                <el-descriptions-item label="问题描述" :span="2">{{ detailTarget.description || '—' }}</el-descriptions-item>
              </el-descriptions>
            </div>

            <div class="info-card">
              <div class="section-title">处理信息</div>
              <el-descriptions :column="2" border>
                <el-descriptions-item label="当前状态">{{ getStatusText(detailTarget.status) }}</el-descriptions-item>
                <el-descriptions-item label="处理方式">{{ detailTarget.handlingMethod || '—' }}</el-descriptions-item>
                <el-descriptions-item label="处理人">{{ formatPersonDisplay(detailTarget.processedBy || detailTarget.assignee) }}</el-descriptions-item>
                <el-descriptions-item label="结单人">{{ formatPersonDisplay(detailTarget.closedBy, '—') }}</el-descriptions-item>
                <el-descriptions-item label="首次跟进">{{ formatDate(detailTarget.firstFollowUpAt) }}</el-descriptions-item>
                <el-descriptions-item label="及时跟进">{{ formatBool(detailTarget.isFollowedInTime) }}</el-descriptions-item>
                <el-descriptions-item label="24小时解决">{{ formatBool(detailTarget.resolvedWithin24h) }}</el-descriptions-item>
                <el-descriptions-item label="重复维修次数">{{ detailTarget.repeatRepairCount ?? 0 }}</el-descriptions-item>
                <el-descriptions-item label="相同配件重复更换">{{ formatBool(detailTarget.hasRepeatedPartChange) }}</el-descriptions-item>
                <el-descriptions-item label="时效提醒" :span="2">{{ getSlaDetailText(detailTarget) }}</el-descriptions-item>
                <el-descriptions-item label="配件到货时间">{{ formatDate(detailTarget.partsArrivedAt) }}</el-descriptions-item>
                <el-descriptions-item label="处理结果" :span="2">{{ detailTarget.processResult || '—' }}</el-descriptions-item>
                <el-descriptions-item label="完工备注" :span="2">{{ detailTarget.completionRemark || '—' }}</el-descriptions-item>
                <el-descriptions-item label="内部备注" :span="2">{{ detailTarget.notes || '—' }}</el-descriptions-item>
              </el-descriptions>
            </div>
          </div>

          <div class="timeline-card">
            <div class="section-title">处理日志</div>
            <div v-if="logs.length > 0" class="log-bubble-track">
              <template v-for="(log, index) in logs" :key="log.id">
                <div class="log-bubble">
                  <div class="log-bubble-time">{{ formatDate(log.createdAt) }}</div>
                  <div class="log-bubble-title">
                    <el-tag size="small" :type="getLogActionTagType(log.actionType)">{{ getActionText(log.actionType) }}</el-tag>
                  </div>
                  <div class="log-bubble-desc">{{ formatLogActionDetail(log.actionDetail) }}</div>
                  <div class="log-bubble-meta">操作人：{{ formatOperatorDisplay(log.operatorName) }}</div>
                </div>
                <div v-if="index < logs.length - 1" class="log-bubble-arrow" aria-hidden="true">-></div>
              </template>
            </div>
            <el-empty v-else description="暂无处理日志" :image-size="80" />
          </div>
        </template>

        <el-empty v-else description="请选择左侧工单查看日志" :image-size="90" />
      </el-card>
    </div>
  </div>
</template>

<script>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { TicketsAPI } from '@/request/tickets'
import { AdminTechniciansAPI } from '@/request/users'
import { useUserStore } from '@/stores/user'
import {
  createWorkOrderStatusOptions,
  createWorkOrderSearchFieldOptions,
  getWorkOrderSearchPlaceholder,
  getWorkOrderSlaMeta,
  getWorkOrderStatusText,
  getWorkOrderStatusType,
  matchesWorkOrderKeyword,
  matchesWorkOrderStatusFilter
} from '@/utils/workOrderUi'

export default {
  name: 'WorkOrderLogPanel',
  setup() {
    const userStore = useUserStore()
    const countdownTimer = ref(null)
    const nowTick = ref(Date.now())
    const loading = ref(false)
    const exporting = ref(false)
    const tickets = ref([])
    const logs = ref([])
    const detailTarget = ref(null)
    const detailCardRef = ref(null)
    const managedUsers = ref([])
    const searchKeyword = ref('')
    const searchField = ref('WORK_ORDER_NO')
    const searchFieldOptions = createWorkOrderSearchFieldOptions('处理内容')
    const statusOptions = createWorkOrderStatusOptions()
    const statusFilter = ref('')
    const currentPage = ref(1)
    const pageSize = 10

    const searchPlaceholder = computed(() => getWorkOrderSearchPlaceholder(searchField.value, searchFieldOptions))

    const filteredTickets = computed(() => {
      const keyword = searchKeyword.value.trim().toLowerCase()
      return tickets.value.filter((item) => {
        if (!matchesWorkOrderStatusFilter(item.status, statusFilter.value)) return false
        return matchesWorkOrderKeyword(item, searchField.value, keyword)
      })
    })

    const paginatedTickets = computed(() => {
      const start = (currentPage.value - 1) * pageSize
      return filteredTickets.value.slice(start, start + pageSize)
    })

    const getStatusText = getWorkOrderStatusText
    const getStatusType = getWorkOrderStatusType

    const getActionText = (action) => ({
      CREATE: '创建工单',
      STATUS_CHANGE: '状态变更',
      ASSIGN: '指派处理',
      PROCESS: '处理记录',
      NOTE: '备注更新',
      UPDATE: '更新工单',
      FOLLOW_UP: '处理记录',
      CLOSE: '结单'
    }[action] || action || '处理日志')

    const getLogActionTagType = (action) => ({
      CREATE: 'success',
      ASSIGN: 'warning',
      STATUS_CHANGE: 'primary',
      PROCESS: 'primary',
      NOTE: 'info',
      UPDATE: 'primary',
      FOLLOW_UP: 'info',
      CLOSE: 'danger'
    }[action] || 'info')

    const formatDate = (value) => {
      if (!value) return '—'
      try {
        return new Intl.DateTimeFormat('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        }).format(new Date(value))
      } catch {
        return String(value)
      }
    }

    const formatBool = (value) => {
      if (value == null) return '—'
      return value ? '是' : '否'
    }

    const formatPersonDisplay = (person, emptyText = '未指派') => {
      if (!person) return emptyText
      const username = String(person.username || '').trim()
      const displayName = String(person.displayName || '').trim()
      if (username && displayName && username !== displayName) {
        return `${username} ${displayName}`
      }
      return username || displayName || emptyText
    }

    const userDisplayMap = computed(() => {
      const map = new Map()
      const register = (person) => {
        if (!person) return
        const username = String(person.username || '').trim()
        const label = formatPersonDisplay(person, '')
        if (username && label) {
          map.set(username, label)
        }
      }
      managedUsers.value.forEach(register)
      register(userStore.userInfo)
      register(detailTarget.value?.createdBy)
      register(detailTarget.value?.processedBy)
      register(detailTarget.value?.assignee)
      register(detailTarget.value?.closedBy)
      return map
    })

    const getUserDisplayByUsername = (username, emptyText = '') => {
      const raw = String(username || '').trim()
      if (!raw) return emptyText
      return userDisplayMap.value.get(raw) || raw
    }

    const formatLogActionDetail = (detail) => {
      const text = String(detail || '').trim()
      if (!text) return '—'
      const transferMatch = text.match(/^处理人由\s+(.+?)\s+转交给\s+(.+)$/)
      if (transferMatch) {
        const fromName = transferMatch[1] === '未指派' ? '未指派' : getUserDisplayByUsername(transferMatch[1], transferMatch[1])
        const toName = transferMatch[2] === '未指派' ? '未指派' : getUserDisplayByUsername(transferMatch[2], transferMatch[2])
        return `处理人由 ${fromName} 转交给 ${toName}`
      }
      const assignMatch = text.match(/^工单已分配给处理人:\s*(.+)$/)
      if (assignMatch) {
        const assigneeName = assignMatch[1] === '未指派' ? '未指派' : getUserDisplayByUsername(assignMatch[1], assignMatch[1])
        return `工单已分配给处理人: ${assigneeName}`
      }
      return text
    }

    const formatOperatorDisplay = (operatorName) => {
      const raw = String(operatorName || '').trim()
      if (!raw) return '系统'
      return getUserDisplayByUsername(raw, raw)
    }

    const getSlaMeta = (row) => getWorkOrderSlaMeta(row, nowTick.value)

    const getSlaType = (row) => getSlaMeta(row).type
    const getSlaLabel = (row) => getSlaMeta(row).label
    const getSlaSubtext = (row) => getSlaMeta(row).subtext
    const getSlaDetailText = (row) => getSlaMeta(row).detail

    const fetchTickets = async () => {
      try {
        loading.value = true
        const list = await TicketsAPI.getAll({ sourceType: 'AFTER_SALES' })
        tickets.value = Array.isArray(list) ? list : []
      } catch (error) {
        ElMessage.error(error?.message || '获取处理日志列表失败')
      } finally {
        loading.value = false
      }
    }

    const loadManagedUsers = async () => {
      try {
        const list = await AdminTechniciansAPI.list('')
        managedUsers.value = Array.isArray(list) ? list : []
      } catch {
        managedUsers.value = []
      }
    }

    const loadDetail = async (row) => {
      if (!row?.id) return
      try {
        const [detail, logList] = await Promise.all([
          TicketsAPI.getById(row.id),
          TicketsAPI.getLogs(row.id)
        ])
        detailTarget.value = detail || row
        logs.value = Array.isArray(logList) ? logList : []
      } catch (error) {
        ElMessage.error(error?.message || '获取处理日志详情失败')
      }
    }

    const handleCurrentChange = async (row) => {
      if (!row?.id) return
      await loadDetail(row)
      // 选中后视角定位到下方的详情卡片
      nextTick(() => {
        const el = detailCardRef.value?.$el
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    }

    const selectAndLoad = (row) => {
      handleCurrentChange(row)
    }

    const refreshDetail = async () => {
      if (!detailTarget.value?.id) return
      await loadDetail(detailTarget.value)
    }

    const exportLogs = async () => {
      try {
        exporting.value = true
        await TicketsAPI.exportProcessLogs({
          keyword: searchKeyword.value.trim() || undefined,
          searchField: searchField.value || undefined,
          displayStatus: statusFilter.value || undefined
        })
        ElMessage.success('处理日志 Excel 已开始下载')
      } catch (error) {
        ElMessage.error(error?.message || '导出处理日志失败')
      } finally {
        exporting.value = false
      }
    }

    onMounted(() => {
      countdownTimer.value = window.setInterval(() => {
        nowTick.value = Date.now()
      }, 60000)
      fetchTickets()
      loadManagedUsers()
    })

    onBeforeUnmount(() => {
      if (countdownTimer.value) {
        window.clearInterval(countdownTimer.value)
        countdownTimer.value = null
      }
    })

    watch(filteredTickets, (list) => {
      const totalPages = Math.max(1, Math.ceil(list.length / pageSize))
      if (currentPage.value > totalPages) {
        currentPage.value = totalPages
      } else {
        currentPage.value = 1
      }
    })

    return {
      userStore,
      loading,
      exporting,
      filteredTickets,
      paginatedTickets,
      detailTarget,
      logs,
      searchKeyword,
      searchField,
      searchFieldOptions,
      statusOptions,
      searchPlaceholder,
      statusFilter,
      currentPage,
      pageSize,
      detailCardRef,
      fetchTickets,
      handleCurrentChange,
      selectAndLoad,
      refreshDetail,
      exportLogs,
      getStatusText,
      getStatusType,
      getSlaType,
      getSlaLabel,
      getSlaSubtext,
      getSlaDetailText,
      getActionText,
      getLogActionTagType,
      formatDate,
      formatBool,
      formatPersonDisplay,
      formatLogActionDetail,
      formatOperatorDisplay
    }
  }
}
</script>

<style scoped>
.log-panel {
  display: grid;
  gap: 16px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.toolbar-input {
  width: 320px;
}

.toolbar-select {
  width: 180px;
}

.log-layout {
  display: grid;
  grid-template-columns: 1fr;
  gap: 18px;
  align-items: start;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
}

.list-card,
.detail-card {
  border-radius: 16px;
  border: 1px solid #e5e7eb;
}

.card-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.card-header-main {
  flex: 1;
}

.detail-card-header .card-header-main {
  text-align: center;
}

.card-title {
  font-size: 17px;
  font-weight: 700;
  color: #0f172a;
}

.card-subtitle {
  margin-top: 4px;
  color: #64748b;
  font-size: 13px;
}

.sla-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sla-subtext {
  color: #64748b;
  font-size: 12px;
  line-height: 1.4;
}

.summary-card {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  margin-bottom: 16px;
  border-radius: 14px;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%);
  border: 1px solid #dbeafe;
}

.summary-title {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
}

.summary-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  color: #64748b;
  font-size: 13px;
}

.summary-assignee {
  min-width: 160px;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #dbeafe;
}

.summary-label {
  color: #94a3b8;
  font-size: 12px;
}

.summary-value {
  margin-top: 8px;
  color: #1e293b;
  font-size: 16px;
  font-weight: 700;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.info-card,
.timeline-card {
  margin-top: 16px;
  padding: 16px;
  border-radius: 14px;
  border: 1px solid #edf2f7;
  background: #fff;
}

.section-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
}

.log-bubble-track {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}

.log-bubble {
  min-width: 220px;
  max-width: 280px;
  padding: 12px 14px;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
}

.log-bubble-time {
  color: #64748b;
  font-size: 12px;
  margin-bottom: 8px;
}

.log-bubble-title {
  margin-bottom: 8px;
}

.log-bubble-desc {
  color: #334155;
  line-height: 1.7;
  word-break: break-word;
}

.log-bubble-meta {
  margin-top: 8px;
  color: #94a3b8;
  font-size: 12px;
}

.log-bubble-arrow {
  color: #94a3b8;
  font-size: 28px;
  line-height: 1;
  font-weight: 500;
}

@media (max-width: 900px) {
  .info-grid {
    grid-template-columns: 1fr;
  }

  .log-bubble-arrow {
    display: none;
  }
}
</style>
